<div class="billing-settings-container">
  <div class="header">
    <h2>Billing settings</h2>
    <p class="subtitle">Configure retainage, liquidated damages, and contract time settings for pay applications.</p>
  </div>

  @if (loading) {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
  }

  @if (initialized && !loading) {
    <form [formGroup]="billingForm" class="billing-form">
      <!-- Layout Mode Section -->
      <div class="section layout-mode-section">
        <div class="flex flex-row justify-between items-center gap-[20px] w-full">
          <div>
            <div class="label text-type-13 uppercase">Use legacy pay app layout</div>
            <span class="toggle-hint">Enable to use the older pay app format without the 4-card summary view</span>
          </div>
          <mat-slide-toggle
            formControlName="use_legacy_payapp_layout"
            [disableRipple]="true"
            class="slide-toggle-btn"
            color="primary"
          ></mat-slide-toggle>
        </div>
      </div>
      <!-- Settings (hidden when legacy mode is enabled) -->
      @if (!useLegacyLayout) {
        <!-- Retainage Section -->
        <div class="section">
          <h3 class="section-title">Retainage settings</h3>
          <app-number-input
            (checkForErrors)="checkForErrors($event)"
            [error]="errorMessages['retainage_percentage']"
            [isDecimalAllowed]="true"
            [decimalPlaces]="2"
            [min]="0"
            [max]="100"
            [parentFormGroup]="billingForm"
            class="w-full mb-[20px]"
            controlName="retainage_percentage"
            hint="Standard retainage held from each payment"
            label="Retainage percentage"
            suffixText="%"
          />
          <div class="flex flex-row gap-[20px] w-full mb-[20px]">
            <app-number-input
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['retainage_threshold']"
              [min]="0"
              [max]="100"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="retainage_threshold"
              hint="% complete when reduced retainage applies"
              label="Retainage threshold"
              suffixText="%"
            />
            <app-number-input
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['reduced_retainage_percentage']"
              [isDecimalAllowed]="true"
              [decimalPlaces]="2"
              [min]="0"
              [max]="100"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="reduced_retainage_percentage"
              hint="Retainage after threshold is reached"
              label="Reduced retainage percentage"
              suffixText="%"
            />
          </div>
        </div>
        <!-- Liquidated Damages Section -->
        <div class="section">
          <h3 class="section-title">Liquidated damages</h3>
          <app-number-input
            (checkForErrors)="checkForErrors($event)"
            [error]="errorMessages['liquidated_damages_per_day']"
            [isDecimalAllowed]="true"
            [decimalPlaces]="2"
            [showCurrency]="true"
            [min]="0"
            [parentFormGroup]="billingForm"
            class="w-full mb-[20px]"
            controlName="liquidated_damages_per_day"
            hint="Daily penalty for each day past contract completion"
            label="Liquidated damages per day"
          />
        </div>
        @if (project?.enable_moh_per_item) {
          <div class="section">
            <h3 class="section-title">Materials on hand</h3>
            <app-dropdown
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['moh_over_contract_policy']"
              [options]="mohOverContractPolicyOptions"
              [parentFormGroup]="billingForm"
              class="w-full"
              controlName="moh_over_contract_policy"
              label="Over contract item total"
              placeholder="Select"
            />
          </div>
        }
        <!-- Contract Dates Section -->
        <div class="section">
          <h3 class="section-title">Contract dates</h3>
          <div class="flex flex-row gap-[20px] w-full mb-[20px]">
            <app-dropdown
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['contract_type']"
              [options]="contractTypeOptions"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="contract_type"
              label="Contract type"
              placeholder="Select"
            />
            <app-date-picker
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['begin_contract_time_date']"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="begin_contract_time_date"
              label="Contract start date"
            />
          </div>
          <div class="flex flex-row gap-[20px] w-full mb-[20px]">
            <app-date-picker
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['actual_start_date']"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="actual_start_date"
              label="Actual start date"
            />
            <app-date-picker
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['notice_to_proceed_date']"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="notice_to_proceed_date"
              label="Notice to proceed date"
            />
          </div>
          <div class="flex flex-row gap-[20px] w-full mb-[20px]">
            <app-number-input
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['original_contract_days']"
              [min]="0"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="original_contract_days"
              hint="Total days in original contract"
              label="Original contract days"
            />
            <app-date-picker
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['substantial_completion_date']"
              [parentFormGroup]="billingForm"
              class="w-1/2"
              controlName="substantial_completion_date"
              label="Substantial completion date"
            />
          </div>
        </div>
        <!-- Working Day Calendar Section -->
        @if (billingForm.get('contract_type')?.value === 'working') {
          <div class="section">
            <h3 class="section-title">Working day calendar</h3>
            <div class="calendar-toolbar">
              <div class="year-nav">
                <button mat-icon-button type="button" class="nav-arrow" (click)="previousYear()">
                  <mat-icon>chevron_left</mat-icon>
                </button>
                <span class="year-label">{{ selectedYear }}</span>
                <button mat-icon-button type="button" class="nav-arrow" (click)="nextYear()">
                  <mat-icon>chevron_right</mat-icon>
                </button>
              </div>
              <span class="toolbar-spacer"></span>
              @if (hasOrgCalendar) {
                <button mat-stroked-button type="button" class="toolbar-btn" (click)="initializeFromOrg()">
                  <mat-icon>content_copy</mat-icon> Copy org holidays
                </button>
              }
              <button mat-stroked-button type="button" class="toolbar-btn" (click)="addHoliday()">
                <mat-icon>add</mat-icon> Add holiday
              </button>
              @if (!isCalendarEmpty) {
                <button mat-icon-button type="button" [matMenuTriggerFor]="calendarMenu">
                  <mat-icon>more_vert</mat-icon>
                </button>
              }
              <mat-menu #calendarMenu="matMenu">
                <button mat-menu-item (click)="clearCalendar()">
                  <mat-icon>delete_outline</mat-icon>
                  <span>Clear all holidays</span>
                </button>
              </mat-menu>
            </div>
            @if (calendarLoading) {
              <div class="calendar-loading">
                <mat-spinner diameter="24"></mat-spinner>
              </div>
            }
            @if (!calendarLoading) {
              <app-year-grid-calendar
                [holidays]="projectHolidays"
                [orgHolidays]="orgHolidays"
                [year]="selectedYear"
                (holidayClick)="onHolidayClick($event)"
                (holidayAdd)="onHolidayAdd($event)"
                (holidayEdit)="onHolidayEdit($event)"
                (holidayRemove)="onHolidayRemove($event)">
              </app-year-grid-calendar>
            }
          </div>
        }
        <!-- Pay App Template Section -->
        <div class="section">
          <div class="section-header">
            <h3 class="section-title">Payapp template</h3>
            <button mat-stroked-button type="button" class="create-template-btn" (click)="openTemplateEditor()">
              <mat-icon>add</mat-icon>
              New project template
            </button>
          </div>
          <div class="template-row flex items-start gap-[12px] mb-[20px]">
            <app-dropdown
              (checkForErrors)="checkForErrors($event)"
              [error]="errorMessages['default_template_id']"
              [options]="templateOptions"
              [parentFormGroup]="billingForm"
              class="flex-1"
              controlName="default_template_id"
              label="Template"
              placeholder="Select"
            />
            <button
              mat-icon-button
              type="button"
              class="preview-button"
              matTooltip="Preview template"
              [disabled]="!canPreview"
              (click)="previewTemplate(billingForm.get('default_template_id')?.value)"
              >
              <mat-icon>visibility</mat-icon>
            </button>
          </div>
          <!-- Project-specific templates list -->
          @if (projectTemplates.length > 0) {
            <div class="project-templates-list">
              <h4 class="subsection-title">Project templates</h4>
              @for (template of projectTemplates; track template) {
                <div class="template-item">
                  <div class="template-item-info">
                    <span class="template-item-name">{{ template.name }}</span>
                    @if (template.description) {
                      <span class="template-item-desc">{{ template.description }}</span>
                    }
                  </div>
                  <div class="template-item-actions">
                    <button mat-icon-button matTooltip="Edit" (click)="openTemplateEditor(template)">
                      <mat-icon>edit</mat-icon>
                    </button>
                    <button mat-icon-button matTooltip="Delete" color="warn" (click)="deleteTemplate(template)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </div>
              }
            </div>
          }
          @if (templatesLoading) {
            <div class="template-loading">
              <mat-spinner diameter="20"></mat-spinner>
              <span>Loading templates...</span>
            </div>
          }
        </div>
      }
      <!-- Actions -->
      <div class="actions">
        <button mat-stroked-button type="button" (click)="onReset()">Reset</button>
        <button mat-flat-button color="primary" type="submit" (click)="onSave()" [disabled]="billingForm.invalid">
          Save settings
        </button>
      </div>
    </form>
  }
</div>
